/*
 *   Copyright (c) 2025 LoginID Inc
 *   All rights reserved.

 *   Licensed under the Apache License, Version 2.0 (the "License");
 *   you may not use this file except in compliance with the License.
 *   You may obtain a copy of the License at

 *   http://www.apache.org/licenses/LICENSE-2.0

 *   Unless required by applicable law or agreed to in writing, software
 *   distributed under the License is distributed on an "AS IS" BASIS,
 *   WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 *   See the License for the specific language governing permissions and
 *   limitations under the License.
 */

import { base64UrlToString, stringToBase64Url } from "@/lib/encoding";
import { Message, MessagingService } from "@/services/messaging";
import { CheckoutConfirmPrompt } from "./CheckoutConfirmPrompt";
import { Center, Card, Flex, Image, useMantineTheme, Button, Loader, Stack, Text } from "@mantine/core";
import CheckoutLoginPrompt from "./CheckoutLoginPrompt";
import { ReceiptRequest, WalletMockService } from "@/services/backend";
import { CALLBACK_URL, WEBVIEW_URL } from "@/lib/urls";
import { useSearchParams, useParams } from "react-router-dom";
import { LIDService } from "@/services/loginid";
import { useEffect, useState } from "react";
import { CheckoutAgentResult } from "./CheckoutAgentResult";
import CheckoutLoginOTP from "./CheckoutLoginOTP";
import { CheckoutAgentProgress } from "./CheckoutAgentProgress";
import { AgenticCheckoutService } from "@/services/checkout";
import Lottie from "lottie-react";
import cartAni from "@/assets/cart.json";
import formAni from "@/assets/form-filling.json";
import paymentAni from "@/assets/payment.json";


export enum CheckoutViewEnum {
  Checkout = "checkout",
  Login = "login",
  Init = "init",
  Confirmation = "confirm",
  AddPasskey = "add-passkey",
  AgentResult = "agent-result",
  AgentProgress = "agent-progress"
}

export interface DisplayMessage {
  text: string;
  type: string;
}

export interface CheckoutRequest {
  merchant: string;
  preid: string;
  subtotal: string;
  shipping: string;
  tax: string;
  total: string;
  desc: string;
  callback: string;
  cid: string;
  productUrl: string;
  items?: CheckoutItem[];
  orderId?: string;
}

export interface CheckoutItem {
  id: string;
  amount: string;
  quantity: number;
  merchant?: string;
  name?: string;
  url?: string;
}

export interface BankingData {
  id: string;
  bank: string;
  amount: string;
  merchant: string;
}

const mService = new MessagingService(window.parent);
const checkout_progress = process.env.REACT_APP_CHECKOUT_PROGRESS || "false"
/**
 * CheckoutPage
 *
 * This component manages the wallet-side flow for LoginID Checkout.
 *
 * Responsibilities:
 * - Listens for messages from the merchant site (in iframe mode) or redirects in webview mode
 * - Loads the user's checkout payload and begins Wallet SDK's `beginFlow` to determine next action
 * - Displays fallback login (bank sign-in, autofill) or transaction confirmation based on passkey availability
 * - Handles communication back to merchant (via message passing or URL redirects) after checkout completes
 *
 * Flow Summary:
 * 1. Load order and determine checkout context (webview or iframe).
 * 2. Use Wallet SDK (`beginFlow`) to decide if user can approve with passkey or needs to fallback login.
 * 3. After user login/approval, send the result back to merchant domain.
 *
 * Related:
 * - `WalletMockService` is used to simulate backend order creation for demo.
 * - `LIDService` wraps the LoginID Wallet SDK client used for authentication and transaction signing.
 */
export function AgenticCheckoutPage() {
  const [username, setUsername] = useState<string>("");
  const [token, setToken] = useState<string>("");
  const [view, setView] = useState<CheckoutViewEnum>(CheckoutViewEnum.Init);
  const [searchParams] = useSearchParams();
  const [webview, setWebview] = useState<boolean>(false);
  const [passkey, setPasskey] = useState<boolean>(false);
  const [order, setOrder] = useState<string>("");
  const [redirect, setRedirect] = useState<boolean>(false);
  const [payload, setPayload] = useState<CheckoutRequest | null>(null);
  const [cid, setCID] = useState<string>("");
  const [initMsg, setInitMsg] = useState<string>("adding item to cart at merchant")
  const [progressImage, setProgressImage] = useState<string>("cart")

  const brand = process.env.REACT_APP_BRAND || "Paze";
  let params = useParams();
  useEffect(() => {
    const inIframe = window.self !== window.top;

    if (inIframe) {
      mService.onMessage((msg, origin) => onMessageHandle(msg, origin));
    } else {
      setRedirect(true);
      console.log("resize");
      window.self.resizeTo(400, 800);
    }

    const query_webview = searchParams.get("webview");
    if (query_webview) {
      setWebview(true);
      if (query_webview !== "null") {
        setView(CheckoutViewEnum.Confirmation);
        setUsername(query_webview);
      }
    }

    loadOrder();
  }, [searchParams]);

  async function loadOrder() {
    let cId = params.id;
    let apayload = null;
    console.log("load order");


    if (cId) {
      const checkoutId = "";
      //await sleep(5000);
      setCID(cId)
      const txPayload = cId;
      const result = await LIDService.client.beginFlow({
        checkoutId: checkoutId,
        txPayload,
      });

      if (result.nextAction === "passkey:tx") {
        apayload = await AgenticCheckoutService.initCheckout(cId);
        setInitMsg("Updating shipping and pricing")
        setProgressImage("form")
        apayload = await AgenticCheckoutService.updateCheckout(cId);
        const orderId = await WalletMockService.setupOrder(apayload);
        setOrder(orderId);
        setPayload(apayload);
        setView(CheckoutViewEnum.Confirmation);
      } else {
        // fallback
        setView(CheckoutViewEnum.Login);
      }
    }
  }

  function onMessageHandle(msg: Message, origin: string) { }

  function renderView(view: CheckoutViewEnum) {
    if (view === CheckoutViewEnum.Confirmation && payload != null) {
      return (
        <CheckoutConfirmPrompt
          username={username}
          token={token}
          request={payload}
          hasPasskey={passkey}
          onComplete={onCheckoutConfirmHandle}
        />
      );
    } else if (view === CheckoutViewEnum.AgentResult && payload != null) {
      return <CheckoutAgentResult username={username} request={payload} token={token} />;
    } else if (view === CheckoutViewEnum.AgentProgress && payload != null) {
      return <CheckoutAgentProgress username={username} request={payload} token={token} onComplete={onAgentConfirmHandle} />;
    } else if (view === CheckoutViewEnum.Init) {
      return <ShowProgress message={initMsg} imageSrc={progressImage} />;
    } else {
      return <CheckoutLoginOTP onComplete={onCheckoutLoginHandle} onExternal={onExternalHandle} />;
    }
  }

  async function onCheckoutLoginHandle(email: string, token: string, next: string) {
    // Display passkey authentication with autofill
    // if payload has preid ... notify websocket
    if (next === "cancel") {
      await AgenticCheckoutService.cancelCheckout(cid);
      mService.sendMessageData({ id: order, passkey: false, error: "user cancel" });
      return;
    }
    if (next === "passkey") {

      let apayload = await WalletMockService.initCheckout(cid);
      const orderId = await WalletMockService.setupOrder(apayload);
      setOrder(orderId);
      setPayload(apayload);

      setUsername(email);
      setToken(token);
      setPasskey(true);
      setView(CheckoutViewEnum.Confirmation);
    }
  }

  function onExternalHandle(bank: string) {
    if (payload) {
      const data: BankingData = {
        id: order,
        bank: bank,
        merchant: payload?.merchant,
        amount: payload?.total,
      };

      document.location.href =
        "/banking?data=" + stringToBase64Url(JSON.stringify(data));
    }

  }
  const isFakeTransaction = true
  const walletUrl = process.env.REACT_APP_WALLET_URL || "https://aw-api.testnet.loginid.io";
  async function onAgentConfirmHandle() {
    setView(CheckoutViewEnum.Init)
    setInitMsg("completing order at merchant")
    setProgressImage("payment")
    if (payload?.preid) {
      const atoken = await WalletMockService.notify(payload);
      let orderId : string | undefined = atoken;
      if(isFakeTransaction) {
        await AgenticCheckoutService.cancelCheckout(cid);
        await sleep(3000);
      } else {
        let apayload = await AgenticCheckoutService.completeCheckout(cid);
        orderId = apayload.orderId
      }

      if (orderId) {
        const receipt: ReceiptRequest = {
          id: orderId,
          orderId: orderId,
          cid: cid,
          at: new Date().toLocaleDateString(),
        }
        await WalletMockService.storeReceipt(receipt);
        setToken(orderId);
        mService.sendMessageData({ id: order, token: orderId, passkey: true });
        setView(CheckoutViewEnum.AgentResult);
        return;
      } else {
        mService.sendMessageData({ id: order, passkey: false, error: "user cancel" });
        return;
      }
    }
  }

  async function onCheckoutConfirmHandle(email: string, token: string, next: string) {

    // if payload has preid ... notify websocket
    if (next === "cancel") {
      await AgenticCheckoutService.cancelCheckout(cid);
      mService.sendMessageData({ id: order, passkey: false, error: "user cancel" });
      return;
    }

    if (payload?.preid) {
      if (checkout_progress === "false") {
        /*
        const atoken = await WalletMockService.notify(payload);
        setToken(token);
        mService.sendMessageData({id:order, token: atoken, passkey: true});
        setView(CheckoutViewEnum.AgentResult);
        */
        onAgentConfirmHandle()
        return;

      } else {

        setView(CheckoutViewEnum.AgentProgress);
        return;
      }
    }

    const hasPasskey = next === "passkey";
    const orderData = { id: order, passkey: hasPasskey };

    const baseCallback = payload?.callback || CALLBACK_URL;
    const encodedData = stringToBase64Url(JSON.stringify(orderData));
    const callbackUrl = `${baseCallback}?data=${encodedData}`;

    if (!redirect) {
      return mService.sendMessageData({
        id: order,
        email: email,
        token: token,
        callback: baseCallback,
      });
    }

    if (webview) {
      const webviewCallback = WEBVIEW_URL;
      const webviewData = stringToBase64Url(JSON.stringify(orderData));
      document.location.href = `${webviewCallback}?data=${webviewData}`;
    } else {
      document.location.href = callbackUrl;
    }
  }
  const theme = useMantineTheme();

  return (
    <Center h="100vh" w="100%">
      <Card shadow="none" w={{ base: "100%", sm: 480, md: 480, lg: 550 }} h="100%" mih={420} p="sm" style={{}}>
        <Flex justify="center" align="center" direction="column" w="100%">
          <Image
            h={28}
            w={96}
            src={`/assets/${brand}/Logo.svg`}
            alt={brand}
            mb="md"
          />
          {renderView(view)}
        </Flex>
      </Card>
    </Center>
  );
}

function ShowProgress(props: { message: string, imageSrc: string }) {
  return (
    <>
      <Stack justify="center" align="center" h="100%">
        <Text>{props.message}</Text>
        <AnimationIcon imageSrc={props.imageSrc}></AnimationIcon>
      </Stack>
    </>
  );
}

function AnimationIcon(props: { imageSrc: string }) {
  if (props.imageSrc === "payment") {
    return (<Lottie animationData={paymentAni} style={{ width: 200 }} />)
  } else if (props.imageSrc === "form") {
    return (<Lottie animationData={formAni} style={{ width: 200 }} />)
  } else {
    return (<Lottie animationData={cartAni} style={{ width: 200 }} />)
  }
}

function sleep(ms: number) {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}
